import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  Container,
  Box,
  TextField,
  Typography,
  Paper,
  useTheme,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddOrganization = () => {
  const theme = useTheme();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    organizationName: '',
    email: '',
    mobileNo: '',
    landlineNo: '',
    address: '',
    PinCode: '',
    website: '',
    fax: '',
    country: '',
    state: '',
    city: '',
  });

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const response = await axios.get(`${API_BASE_URL}/api/master/countries/`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        setCountries(response.data);
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      if (formData.country) {
        try {
          const accessToken = localStorage.getItem('accessToken');
          const response = await axios.get(
            `${API_BASE_URL}/api/master/states/?country=${formData.country}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          // Filter states by the selected country
          const filteredStates = response.data.filter(
            (state) => state.country === parseInt(formData.country)
          );
          setStates(filteredStates);
          setFormData((prevData) => ({ ...prevData, state: '', city: '' }));
        } catch (err) {
          console.error("Failed to fetch states:", err);
        }
      }
    };
    fetchStates();
  }, [formData.country]);

  useEffect(() => {
    const fetchCities = async () => {
      if (formData.state) {
        try {
          const accessToken = localStorage.getItem('accessToken');
          const response = await axios.get(
            `${API_BASE_URL}/api/master/cities/?state=${formData.state}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          // Filter cities by the selected state
          const filteredCities = response.data.filter(
            (city) => city.state === parseInt(formData.state)
          );
          setCities(filteredCities);
          setFormData((prevData) => ({ ...prevData, city: '' }));
        } catch (err) {
          console.error("Failed to fetch cities:", err);
        }
      }
    };
    fetchCities();
  }, [formData.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.organizationName || !formData.email || !formData.mobileNo ||
      !formData.address || !formData.PinCode || !formData.website ||
      !formData.country || !formData.state || !formData.city) {
      setSnackbarMessage('Please fill all required fields');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    try {
      setLoading(true);
      const accessToken = localStorage.getItem('accessToken');
let website = formData.website.trim();

if (
  website &&
  !website.startsWith('http://') &&
  !website.startsWith('https://')
) {
  website = `https://${website}`;
}
      const payload = {
        name: formData.organizationName,
        email: formData.email,
        mobile: formData.mobileNo,
        landline: formData.landlineNo,
        address: formData.address,
        zipcode: formData.PinCode,
       website: website,
        fax: formData.fax,
        country: formData.country,
        state: formData.state,
        city: formData.city
      };

      await axios.post(
        `${API_BASE_URL}/api/master/organizations/`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      setSnackbarMessage('Organization added successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);

      setFormData({
        organizationName: '',
        email: '',
        mobileNo: '',
        landlineNo: '',
        address: '',
        PinCode: '',
        website: '',
        fax: '',
        country: '',
        state: '',
        city: '',
      });

      setTimeout(() => {
        navigate(-1);
      }, 1000);
    } catch (err) {
      // Check if there's a response with error data
      if (err.response?.data) {
        // Get the first error message from the response
        const errorData = err.response.data;
        const firstErrorKey = Object.keys(errorData)[0];
        const errorMessage = errorData[firstErrorKey];

        // If errorMessage is an array, get the first element
        if (Array.isArray(errorMessage)) {
          setSnackbarMessage(errorMessage[0]);
        } else {
          setSnackbarMessage(errorMessage);
        }
      } else {
        setSnackbarMessage(err.response?.data?.message || 'Failed to add organization');
      }

      setSnackbarSeverity('error');
      setOpenSnackbar(true);

      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleReset = () => {
    setFormData({
      organizationName: '',
      email: '',
      mobileNo: '',
      landlineNo: '',
      address: '',
      PinCode: '',
      website: '',
      fax: '',
      country: '',
      state: '',
      city: '',
    });
  };

  const isFormValid = () => {
    return formData.organizationName &&
      formData.email &&
      formData.mobileNo &&
      formData.address &&
      formData.PinCode &&
      formData.website &&
      formData.country &&
      formData.state &&
      formData.city;
  };

  // Common style for all form fields
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const selectSx = {
    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  };

  return (
    <Container maxWidth="lg" sx={{ my: 0 }}>
      <Box
        component={Paper}
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Add New Organization
          </Typography>
        </Box>

        <form onSubmit={handleSave}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label="Organization Name *"
              variant="outlined"
              fullWidth
              name="organizationName"
              value={formData.organizationName}
              onChange={handleChange}
              required
              sx={textFieldSx}
            />
            <TextField
              label="Email *"
              variant="outlined"
              fullWidth
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              sx={textFieldSx}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label="Mobile No. *"
              variant="outlined"
              fullWidth
              name="mobileNo"
              value={formData.mobileNo}
              onChange={handleChange}
              inputProps={{ maxLength: 10 }}
              required
              sx={textFieldSx}
            />
            <TextField
              label="Landline No."
              variant="outlined"
              fullWidth
              name="landlineNo"
              value={formData.landlineNo}
              onChange={handleChange}
              inputProps={{ maxLength: 15 }}
              sx={textFieldSx}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label="Address *"
              variant="outlined"
              fullWidth
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              sx={textFieldSx}
            />
            <TextField
              label="Pin Code *"
              variant="outlined"
              fullWidth
              name="PinCode"
              value={formData.PinCode}
              onChange={handleChange}
              required
              sx={textFieldSx}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label="Website *"
              variant="outlined"
              fullWidth
              name="website"
              value={formData.website}
              onChange={handleChange}
              required
              sx={textFieldSx}
            />
            <TextField
              label="Fax"
              variant="outlined"
              fullWidth
              name="fax"
              value={formData.fax}
              onChange={handleChange}
              sx={textFieldSx}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <FormControl fullWidth required>
              <InputLabel sx={{ color: theme.palette.text.primary }}>Country *</InputLabel>
              <Select
                name="country"
                value={formData.country}
                onChange={handleChange}
                label="Country *"
                sx={selectSx}
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel sx={{ color: theme.palette.text.primary }}>State *</InputLabel>
              <Select
                name="state"
                value={formData.state}
                onChange={handleChange}
                label="State *"
                disabled={!formData.country || states.length === 0}
                sx={selectSx}
              >
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <FormControl fullWidth required>
              <InputLabel sx={{ color: theme.palette.text.primary }}>City *</InputLabel>
              <Select
                name="city"
                value={formData.city}
                onChange={handleChange}
                label="City *"
                disabled={!formData.state || cities.length === 0}
                sx={selectSx}
              >
                {cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="contained"
              type="submit"
              onClick={handleSave}
              disabled={!isFormValid() || loading}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
              }}
            >
              {loading ? 'SAVING...' : 'SAVE'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              disabled={loading}
            >
              RESET
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancel}
              disabled={loading}
            >
              BACK
            </Button>
          </Box>
        </form>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddOrganization;